/**
 * Quote Request Data Hook
 *
 * Fetches quote request details for auto-quote submission.
 * Provides item information needed to generate proper quote response DTO.
 */

import { useExternalQuoteRequestsControllerFindOne } from "@semo/api-contracts";
import type { BaseResponseDto } from "@semo/api-contracts";

/**
 * Unwrap BaseResponseDto to get actual data
 */
function unwrapResponse<T>(response: unknown): T | undefined {
  return (response as BaseResponseDto & { data?: T })?.data;
}

/**
 * Quote request item (simplified for auto-quote)
 */
export interface QuoteRequestItem {
  name: string;
  quantity: number;
  unit: string;
  specifications?: string;
}

/**
 * Quote request data (minimal info needed for auto-quote)
 */
export interface QuoteRequestData {
  id: string;
  projectName: string;
  items: QuoteRequestItem[];
  totalAmount?: number;
}

/**
 * Hook for fetching quote request data
 *
 * Used by auto-quote flow to get item details before submission.
 *
 * @param quoteRequestId - Quote request UUID from URL
 * @returns Quote request data, loading state, and error
 */
export const useQuoteRequestData = (quoteRequestId: string | null) => {
  const query = useExternalQuoteRequestsControllerFindOne(
    quoteRequestId || "",
    {
      query: {
        enabled: !!quoteRequestId, // Only fetch if ID is provided
      },
    },
  );

  // Extract and transform data
  const quoteRequest: QuoteRequestData | null = query.data
    ? {
        id: quoteRequestId || "",
        projectName:
          unwrapResponse<{ projectName?: string }>(query.data)?.projectName ||
          "견적 요청",
        items:
          unwrapResponse<{ items?: QuoteRequestItem[] }>(
            query.data,
          )?.items?.map((item) => ({
            name: item.name || "품목",
            quantity: item.quantity || 1,
            unit: item.unit || "개",
            specifications: item.specifications,
          })) || [],
      }
    : null;

  return {
    quoteRequest,
    isLoading: query.isLoading,
    error: query.error,
  };
};
